import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import BreadcrumbsNav from "../common-component/BreadcrumbsNav";
import { Icons } from "./Machine360Icons";
import { commonservices } from "../../services/CommonServices";

// Map machine status to CSS class (matching component.css: pending, in-process, rejected, publish/approved, deprecated, discontinued)
const getMachineStatusClass = (status) => {
  if (!status) return "publish";
  const mapped = commonservices.MachineStatus(status);
  if (mapped) return mapped;
  const normalized = String(status).trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized === "pending") return "pending";
  if (normalized === "inprocess") return "in-process";
  if (normalized === "rejected") return "rejected";
  if (normalized === "publish" || normalized === "approved") return "publish approved";
  if (normalized === "deprecated") return "deprecated";
  if (normalized === "discontinued") return "discontinued";
  return "publish";
};

// Map machine status to border line color matching MyMachine & MarketPlaceDetail
const getMachineStatusColor = (status) => {
  const statusClass = getMachineStatusClass(status);
  if (statusClass.includes("pending")) return "#ffd700";
  if (statusClass.includes("in-process")) return "#2b8dc8";
  if (statusClass.includes("rejected")) return "#df0404";
  if (statusClass.includes("deprecated")) return "#ff4500";
  if (statusClass.includes("discontinued")) return "#c00d0d";
  return "#008000"; // publish / approved
};

export const Machine360Header = ({
  headerData,
  isLoading = false,
  pageName = "",
}) => {
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [headerData?.logo]);

  const statusClass = getMachineStatusClass(headerData?.status);
  const statusColor = getMachineStatusColor(headerData?.status);

  const rawPageName = (headerData?.pageName || pageName || "").toLowerCase();
  const isDashboard = rawPageName.includes("dashboard");
  const isMyMachine =
    rawPageName.includes("machine") || rawPageName === "my machine";
  const parentLabel = isDashboard
    ? "Dashboard"
    : isMyMachine
    ? "My Machine"
    : headerData?.pageName || pageName || "Marketplace";
  const parentPath = isDashboard
    ? "/dashboard"
    : isMyMachine
    ? "/machine"
    : "/marketplace";

  const breadcrumbNavList = [
    {
      name: parentLabel,
      link: parentPath,
      active: false,
    },
    {
      name: headerData?.name ? (
        headerData.name
      ) : isLoading ? (
        <Skeleton width={180} height={16} />
      ) : (
        "Machine Details"
      ),
      active: true,
    },
  ];

  return (
    <div className="m360-header-wrapper">
      {/* Breadcrumb Row matching Image 2 */}
      <div className="m360-breadcrumb-row" aria-label="Breadcrumb">
        <BreadcrumbsNav pageNav={breadcrumbNavList} />
      </div>

      {/* Main Hero Header */}
      {isLoading ? (
        <div className="m360-hero-card">
          {/* Left Side: Avatar + Details Skeleton */}
          <div className="m360-hero-left">
            <div className="m360-avatar-container">
              <div className="m360-avatar-inner" style={{ border: "none", background: "transparent" }}>
                <Skeleton circle={true} height={44} width={44} />
              </div>
            </div>

            <div className="m360-hero-info" style={{ width: "100%", maxWidth: 520 }}>
              {/* Title + Badges Skeleton */}
              <div className="m360-title-row" style={{ gap: 8 }}>
                <Skeleton height={28} width={260} />
                <Skeleton height={20} width={48} borderRadius={4} />
                <Skeleton height={20} width={42} borderRadius={4} />
              </div>

              {/* Description Skeleton */}
              <div className="m360-description my-2">
                <Skeleton height={14} width="92%" />
                <Skeleton height={14} width="70%" />
              </div>

              {/* Tags Skeleton */}
              <div className="m360-tags-row">
                <Skeleton height={22} width={75} borderRadius={12} />
                <Skeleton height={22} width={85} borderRadius={12} />
                <Skeleton height={22} width={95} borderRadius={12} />
              </div>
            </div>
          </div>

          {/* Right Side: Metadata Skeleton */}
          <div className="m360-hero-right">
            <div className="m360-meta-grid">
              <div className="m360-meta-item">
                <Skeleton height={11} width={40} />
                <Skeleton height={14} width={45} />
              </div>
              <div className="m360-meta-item">
                <Skeleton height={11} width={75} />
                <Skeleton height={14} width={95} />
              </div>
              <div className="m360-meta-item">
                <Skeleton height={11} width={70} />
                <Skeleton height={14} width={85} />
              </div>
              <div className="m360-meta-item">
                <Skeleton height={11} width={45} />
                <Skeleton height={14} width={40} />
              </div>
            </div>
          </div>
        </div>
      ) : !headerData ? null : (
        <div className="m360-hero-card">
          {/* Left Side: Avatar + Details */}
          <div className="m360-hero-left">
            <div
              className={`m360-avatar-container ${statusClass}`}
              style={{ borderColor: statusColor }}
              title={headerData?.status ? `Status: ${headerData.status}` : undefined}
            >
              <div className="m360-avatar-inner">
                {headerData.logo && !logoError ? (
                  <img
                    src={headerData.logo}
                    alt={headerData.name}
                    className="m360-avatar-img"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <Icons.Gear size={30} className="m360-avatar-icon" />
                )}
              </div>
            </div>

            <div className="m360-hero-info">
              {/* Title + Badges */}
              <div className="m360-title-row">
                <h1 className="m360-machine-title">{headerData.name}</h1>
                {headerData.version && (
                  <span className="m360-version-badge">{headerData.version}</span>
                )}
                <span className="m360-rating-badge">
                  <span className="m360-star">★</span> {headerData.rating ?? 0}
                </span>
              </div>

              {/* Description */}
              <p className="m360-description">{headerData.description}</p>

              {/* Tags */}
              <div className="m360-tags-row">
                {headerData.tags?.map((tag, idx) => (
                  <span key={idx} className="m360-tag-pill">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side: Metadata Row (Price, Developed by, Last Updated, Version) */}
          <div className="m360-hero-right">
            <div className="m360-meta-grid">
              <div className="m360-meta-item">
                <span className="m360-meta-label">Price</span>
                <span className="m360-meta-value">{headerData.pricing || "Free"}</span>
              </div>
              <div className="m360-meta-item">
                <span className="m360-meta-label">Developed by</span>
                <span className="m360-meta-value">{headerData.developer?.name || "-"}</span>
              </div>
              <div className="m360-meta-item">
                <span className="m360-meta-label">Last Updated</span>
                <span className="m360-meta-value">{headerData.developer?.lastUpdated || "-"}</span>
              </div>
              <div className="m360-meta-item">
                <span className="m360-meta-label">Version</span>
                <span className="m360-meta-value">
                  {headerData.developer?.version || headerData.version || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

